"use client";

import * as React from "react";
import { Funnel as Filter, X } from 'lucide-react';

import { Link } from "@/components/common/Link";
import {
  AddToCartButton,
  EcommerceProductsProvider,
  ProductSortDropdown,
  ProductsFilter,
  useEcommerceProducts,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: any;

const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
const wp_query = new WP_Query(currentWPQueryParams);

function DrawnCircle({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 120 60"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M60 8 C 25 8, 8 22, 8 30 C 8 44, 35 52, 62 52 C 95 52, 112 42, 112 30 C 112 18, 90 9, 60 9" />
    </svg>
  );
}

function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    currentPage,
    totalPages,
    setPage,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
  } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            data-index={i}
            className="h-96 rounded-lg bg-card/60 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center">
        <p className="text-sm text-muted-foreground">
          {"We couldn't load our available kittens."}
        </p>
        <Button
          variant="link"
          onClick={refetch}
          className="mt-2 text-primary"
        >
          Try again
        </Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card/50 p-12 text-center">
        <p style={{ fontFamily: "var(--font-serif)" }} className="text-2xl text-secondary">
          No kittens currently available
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          Check back soon or join our waiting list for future litters.
        </p>
      </div>
    );
  }

  
  const rotations = ["-rotate-1", "rotate-0", "rotate-1", "-rotate-1", "rotate-1", "rotate-0"];

  const pageNumbers = (): (number | "ellipsis")[] => {
    const pages: (number | "ellipsis")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    if (currentPage > 3) pages.push("ellipsis");
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push("ellipsis");
    pages.push(totalPages);
    return pages;
  };

  return (
    <div className="space-y-12">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => {
          const rot = rotations[i % rotations.length];
          const ageCategory = product.categories?.[0]?.name ?? "";
          return (
            <article
              key={product.id}
              data-index={i}
              className={`group relative flex flex-col rounded-2xl border border-border bg-card p-5 pb-7 shadow-[8px_8px_0px_0px_#C97B82] transition-all duration-[350ms] ease-out hover:rotate-0 hover:shadow-lg ${rot}`}
              style={{ transitionTimingFunction: "cubic-bezier(.2,.7,.2,1)" }}
            >
              <Link to={product.link} className="block overflow-hidden rounded-lg bg-muted">
                <div className="aspect-[4/5] overflow-hidden">
                  {product.featured_image_url ? (
                    <img
                      src={product.featured_image_url}
                      alt={product.title}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-accent text-muted-foreground">
                      <span className="text-sm">Catsy Cats</span>
                    </div>
                  )}
                </div>
              </Link>

              <div className="mt-5 flex flex-1 flex-col">
                <Link to={product.link} className="block">
                  <h3
                    className="text-xl font-default font-medium leading-tight text-foreground"
                    style={{ fontFamily: "var(--font-serif)", letterSpacing: "-0.01em" }}
                  >
                    {product.title}
                  </h3>
                </Link>
                {ageCategory && (
                  <p
                    className="mt-1 text-sm text-muted-foreground"
                    style={{ fontFamily: "var(--font-sans)" }}
                  >
                    {ageCategory}
                  </p>
                )}

                <div className="mt-4 flex items-end justify-between gap-3">
                  <div className="relative inline-flex items-center">
                    <DrawnCircle className="absolute -inset-x-3 -inset-y-2 h-[60px] w-[120px] text-primary" />
                    <span
                      className="relative z-10 text-2xl text-primary font-mono"
                      style={{ fontWeight: 600 }}
                    >
                      {"$"}
                      {typeof product.price === "number"
                        ? product.price.toFixed(2)
                        : product.price}
                    </span>
                  </div>

                  <AddToCartButton
                    productId={product.id}
                    variant="default"
                    size="sm"
                    className="w-auto"
                    buttonClassName="bg-primary text-primary-foreground rounded-full px-5"
                    text="Meet"
                    showQuantity={false}
                  />
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent className="gap-2">
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (hasPrev) prevPage();
                }}
                className={!hasPrev ? "pointer-events-none opacity-40" : ""}
              />
            </PaginationItem>

            {pageNumbers().map((p, idx) =>
              p === "ellipsis" ? (
                <PaginationItem key={`e-${idx}`} data-index={idx}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={p} data-index={idx}>
                  <div className="relative inline-flex items-center justify-center">
                    {p === currentPage && (
                      <DrawnCircle className="pointer-events-none absolute h-[60px] w-[60px] text-primary" />
                    )}
                    <PaginationLink
                      href="#"
                      isActive={p === currentPage}
                      onClick={(e) => {
                        e.preventDefault();
                        setPage(p);
                      }}
                      className={
                        p === currentPage
                          ? "relative z-10 border-transparent bg-transparent text-foreground shadow-none hover:bg-transparent"
                          : "border-transparent bg-transparent text-muted-foreground hover:bg-accent"
                      }
                    >
                      {p}
                    </PaginationLink>
                  </div>
                </PaginationItem>
              )
            )}

            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (hasNext) nextPage();
                }}
                className={!hasNext ? "pointer-events-none opacity-40" : ""}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}

export default function Shop() {
  return (
    <section data-section-id="20450" id="shop-content" className="bg-background text-foreground py-16 md:py-20">
      <div className="mx-auto max-w-[1240px] px-6 md:px-8">
        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="flex flex-col gap-8 lg:flex-row lg:gap-8">
            {}
            <aside className="hidden shrink-0 lg:block lg:w-1/4">
              <div className="sticky top-24">
                <div
                  className="rounded-lg border border-border bg-background p-6"
                  style={{
                    borderStyle: "dashed",
                    borderWidth: "1.5px",
                  }}
                >
                  <div className="mb-5 flex items-center justify-between">
                    <h2
                      className="text-lg text-foreground font-default"
                      style={{ fontFamily: "var(--font-serif)", fontWeight: 500 }}
                    >
                      Refine Search
                    </h2>
                    <span
                      className="text-xs uppercase tracking-[0.15em] text-primary"
                      style={{ fontFamily: "var(--font-sans)", fontWeight: 700 }}
                    >
                      Filters
                    </span>
                  </div>
                  <ProductsFilter />
                </div>
              </div>
            </aside>

            {}
            <div className="w-full lg:w-3/4">
              {}
              <div className="mb-6 flex items-center justify-between gap-3">
                <div className="lg:hidden">
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button
                        variant="outline"
                        className="gap-2 rounded-lg border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground"
                      >
                        <Filter className="h-4 w-4" />
                        <span style={{ fontFamily: "var(--font-serif)" }} className="italic">
                          filter
                        </span>
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-full max-w-sm overflow-y-auto bg-background">
                      <SheetHeader>
                        <SheetTitle
                          style={{ fontFamily: "var(--font-serif)", fontWeight: 500 }}
                          className="text-foreground"
                        >
                          Refine Search
                        </SheetTitle>
                      </SheetHeader>
                      <div className="px-4 pb-6">
                        <ProductsFilter />
                      </div>
                    </SheetContent>
                  </Sheet>
                </div>

                <div className="ml-auto">
                  <ProductSortDropdown label="Sort by" />
                </div>
              </div>

              <ProductsGridConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}